<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import Logo from '@/assets/Apexx_LOGO.webp';
import { hubPinia } from '@/pinia';
import { useHubAuthStore } from '@/store/hubAuthStore';
import {
	consumeLastKeycloakOAuthError,
	formatKeycloakOAuthError,
	isKeycloakLogoutInProgress,
} from '@apexx/auth';
import { Button, Icon, Typography, Wizard } from '@apexx/component-library';
import { motion } from 'motion-v';

;

export type UserType = 'client' | 'staff';

const authStore = useHubAuthStore(hubPinia);
const router = useRouter();
const signInError = ref<string>();

function selectUserType(type: UserType): void {
	void router.push({ name: 'login-redirect', query: { type } });
}

function gotoHome(): void {
	void router.push({ name: 'intro' });
}

onMounted(async () => {
	const oauthError = consumeLastKeycloakOAuthError();
	if (oauthError) {
		signInError.value = formatKeycloakOAuthError(oauthError);
	}

	// Never bounce to launcher while logout is tearing down (or if the hub bearer is gone).
	if (isKeycloakLogoutInProgress() || !authStore.isLoggedIn) {
		return;
	}

	await router.replace({ name: 'launcher' });
});
</script>

<template>
	<Wizard
		content-padding="lg"
		centered
		root-class="min-h-dvh"
		content-class="relative pb-[calc(2.5rem+var(--safe-area-bottom))]"
	>
		<template #header>
			<div class="flex flex-col items-center justify-between gap-10 py-20">
				<div class="relative">
					<button
						type="button"
						class="absolute top-4 -left-10 z-10 flex h-10 w-10 items-center justify-center text-white/80 hover:text-white"
						aria-label="Go back to home"
						@click="gotoHome"
					>
						<Icon name="arrowLeft" />
					</button>
					<img
						:src="Logo"
						alt="Apexx logo"
						class="h-16 overflow-hidden rounded"
					/>
				</div>
			</div>
		</template>

		<motion.div
			:initial="{ opacity: 0, y: 20 }"
			:animate="{ opacity: 1, y: 0 }"
			:transition="{ duration: 0.3 }"
			class="mt-10 flex w-full max-w-4xl grow flex-col gap-5"
		>
			<div
				v-if="signInError"
				class="mb-2 rounded-lg bg-error/10 px-4 py-3 text-left"
			>
				<Typography variant="body" class="text-error">
					Sign in failed: {{ signInError }}
				</Typography>
			</div>
			<Typography variant="display" class="self-center"
				>Welcome</Typography
			>
			<Typography
				variant="body"
				class="mt-3 self-center text-on-surface-muted"
			>
				Choose your login type to continue
			</Typography>
			<Button
				variant="primary"
				size="lg"
				icon="person"
				:full-width="true"
				class="mx-auto max-w-md text-white"
				@click="selectUserType('client')"
			>
				Customer Login
			</Button>

			<Button
				variant="light"
				class="mx-auto max-w-md text-on-surface-muted!"
				size="lg"
				:full-width="true"
				@click="selectUserType('staff')"
			>
				Not a Customer?
				<span class="inline! text-primary">Employee Login</span>
			</Button>
		</motion.div>

		<template #footer>
			<div
				class="fixed right-5 bottom-10 z-50 rounded-full bg-success p-2 opacity-80 hover:opacity-100"
			>
				<a
					href="https://wa.me/971526497899"
					target="_blank"
					rel="noopener noreferrer"
					class="flex h-full w-full items-center justify-center"
					aria-label="Contact support on WhatsApp"
				>
					<svg
						viewBox="0 0 32 32"
						class="h-10 w-10 fill-white"
						aria-hidden="true"
					>
						<path
							d=" M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.244-.73.244-1.088 0-.058 0-.144-.03-.215-.1-.172-2.434-1.39-2.678-1.39zm-2.908 7.593c-1.747 0-3.48-.53-4.942-1.49L7.793 24.41l1.132-3.337a8.955 8.955 0 0 1-1.72-5.272c0-4.955 4.04-8.995 8.997-8.995S25.2 10.845 25.2 15.8c0 4.958-4.04 8.998-8.998 8.998zm0-19.798c-5.96 0-10.8 4.842-10.8 10.8 0 1.964.53 3.898 1.546 5.574L5 27.176l5.974-1.92a10.807 10.807 0 0 0 16.03-9.455c0-5.958-4.842-10.8-10.802-10.8z"
							fill-rule="evenodd"
						/>
					</svg>
				</a>
			</div>
		</template>
	</Wizard>
</template>
